import { useCallback, useEffect, useState } from "react";
import Script from "next/script";
import { Box, Stack, Typography } from "@mui/material";
import { RawTimeZone } from "@vvo/tzdb";
import { formatInTimeZone } from "date-fns-tz";
import Head from "next/head";
import { useRouter } from "next/router";
import { useCookies } from "react-cookie";
import styles from "styles/Home.module.css";
// components
import Container from "components/container";
import QuestionHeader from "components/header";
import AvailableSlots from "components/services/availableSlots";
import InvalidServiceAlert from "components/services/invalid-service-alert";
import SchedulePicker from "components/services/schedulePicker";

import Staff from "components/services/staff";
import States from "components/services/states";
import TimeZones from "components/services/zone";
import {
  AvailableSlotsNew as Slot,
  bookingInfo,
  BookingType,
} from "utils/booking";
// context
import {
  generateToken,
  getPatientInfo,
  getServices,
} from "@/utils/services/services-helper";
import { useBookingContext } from "hooks/useBooking";
import {
  BookingContextType,
  ServiceCommon,
  ServiceInfoType,
  ServiceType,
} from "utils/booking";
import { getTrackers } from "utils/utmParser";

import { getDefaultTz } from "@/utils/calendarHelper";
import { getDetailsFromQs } from "@/utils/services/patient.dto";
import InsuranceList from "components/services/insurance-list";
import ReferralStateAlert from "components/services/referral-state-alert";
// =======================================================

type Consult = {
  slots: Slot[];
  loading: boolean;
  selectedDate: string;
};

const PickSlot = ({
  services,
  info,
  patient,
  staffId,
  isITH,
}: {
  services: ServiceType[];
  patient: Partial<ServiceCommon>;
  info: ServiceInfoType;
  staffId?: string;
  isITH: boolean;
}) => {
  const { push, query } = useRouter();
  const router = useRouter();
  const context = useBookingContext() as BookingContextType;
  const cookies = useCookies();
  const [dpc, setDpc] = useState<ServiceType | null>(null);
  const [timeZone, setTimeZone] = useState<RawTimeZone | null>(null);
  const [useAll, setUseAll] = useState(false);
  const [showReferralOption, setShowReferralOption] = useState(false);
  const [consult, setConsult] = useState<Consult>({
    slots: [],
    loading: true,
    selectedDate: formatInTimeZone(
      new Date(),
      getDefaultTz(Intl.DateTimeFormat().resolvedOptions().timeZone).name,
      "yyyy-MM-dd",
    ),
  });

  const [showState, setShowState] = useState(false);
  const [freeConsult, setFreeConsult] = useState<ServiceCommon>(
    bookingInfo.data.freeConsult,
  );
  useEffect(() => {
    const tz =
      getDefaultTz(Intl.DateTimeFormat().resolvedOptions().timeZone) ?? null;

    if (tz)
      setConsult((prev) => ({
        ...prev,
        selectedDate: formatInTimeZone(new Date(), tz.name, "yyyy-MM-dd"),
      }));

    setTimeZone(tz);
  }, []);

  useEffect(() => {
    if (info?.service_name && info?.State_Required && !query.state) {
      setShowState(true);
    } else {
      setShowState(false);
    }
    if (services.length === 0 && query.state && !info?.service_name) {
      router.push("/out-of-service");
    } else if (services.length === 0 && query.state && info?.service_name) {
      setShowReferralOption(true);
    }
  }, [cookies, info, query, query.state, router, services.length]);

  useEffect(() => {
    if (services.length === 1) {
      setDpc(services && services?.length === 1 ? services[0] : null);
    } else if (services.length > 1 && staffId) {
      const tmp = services.find((s) => s.staff_id === staffId) ?? null;
      setDpc(tmp);
    }

    const coupon = query?.promo
      ? String(query?.promo)
      : query?.couponCode
        ? String(query?.couponCode)
        : "";
    const dpc = query?.dpc ? Boolean(query?.dpc) : false;
    const trackers = getTrackers(cookies, query);
    setUseAll(services.length > 1 && !staffId);

    if (patient?.patientId && !query?.repick) {
      setFreeConsult((prev) => ({
        ...prev,
        ...patient,
        preloaded: true,
        cost: info.Cost,
        item_id: info.Books_Item_ID,
        fromSA: false,
        repick: false,
        couponCode: coupon ?? "",
        payment_skipped: dpc,
        ...trackers,
      }));
    } else if (query?.repick) {
      setFreeConsult((prev) => ({
        ...prev,
        ...context.data.freeConsult,
        repick: true,
        preloaded: false,
        fromSA: false,
        cost: info.Cost,
        item_id: info.Books_Item_ID,
        couponCode: coupon ?? "",
        payment_skipped: dpc,
      }));
    } else if (context?.data?.freeConsult?.fromSA) {
      const patient = getDetailsFromQs(query);

      setFreeConsult((prev) => ({
        ...prev,
        ...context?.data.freeConsult,
        ...patient,
        repick: false,
        preloaded: false,
        fromSA: true,
        cost: info?.Cost ?? 0,
        item_id: info?.Books_Item_ID ?? null,
        couponCode: coupon ?? "",
        payment_skipped: dpc,
        ...trackers,
      }));
    } else {
      const patient = getDetailsFromQs(query);
      setFreeConsult((prev) => ({
        ...prev,
        ...bookingInfo.data.freeConsult,
        ...patient,
        repick: false,
        preloaded: false,
        fromSA: query.fromsa ? true : false,
        cost: info?.Cost ?? 0,
        item_id: info?.Books_Item_ID ?? null,
        couponCode: coupon ?? "",
        payment_skipped: dpc,
        ...trackers,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    services,
    patient,
    query?.repick,
    context,
    context.data.freeConsult,
    staffId,
    info.Cost,
    info.Books_Item_ID,
    query,
  ]);

  useEffect(() => {
    if (router.query?.repick) return;

    const trackers = getTrackers(cookies, router.query);
    const { data } = bookingInfo;
    // eslint-disable-next-line no-unused-vars
    const { additionalQuestions, requiredDocuments, ...rest } = data;
    const payload: BookingType = {
      data: {
        ...rest,
        additionalQuestions: info?.Additional_Questions
          ? info?.Additional_Questions
          : [],
        freeConsult,
        requiredDocuments: info?.required_documents
          ? info?.required_documents
          : [],
        services,
        serviceInfo: info,
        ...trackers,
      },
    };
    context.reset(payload);
  }, [context, patient, cookies, router.query, info, freeConsult, services]);

  const handleChange = useCallback(
    (slots: Slot[], loading: boolean, selectedDate: string) => {
      setConsult({
        slots: slots ?? [],
        loading: loading ?? false,
        selectedDate,
      });
    },
    [],
  );

  const handleSetDpc = (dpc: ServiceType | null) => {
    setDpc(dpc);
    setUseAll(dpc === null);
  };

  const handleChangeTz = useCallback((tz: RawTimeZone | null) => {
    setTimeZone(tz);
  }, []);

  const generateQsString = () => {
    const keys = Object.keys(query);
    let qs = "";
    for (let i = 0; i <= keys.length - 1; i++) {
      qs =
        i === 0
          ? `${qs}${keys[i]}=${query[keys[i]]}`
          : `${qs}&${keys[i]}=${query[keys[i]]}`;
    }
    return qs;
  };
  const onInsuranceSelected = (insurance: string) => {
    router.prefetch(`${router.asPath}&payor=${insurance}`);
  };

  const onInsuranceSubmitted = (insurance: string) => {
    router.push(`${router.asPath}&payor=${insurance}`);
  };

  const showProviders =
    Boolean(router.query?.showprovider) === true ||
    !String(router.query?.service).includes(process.env.ITH_SERVICE_ID!);

  const selectedState = query?.state && query?.state !== "" ? query?.state : "";

  const showInsurance =
    query.payor === undefined && info?.Insurance_Required && !showState;

  const showPicker = !showState && !showInsurance;

  if (showReferralOption)
    return (
      <div className={styles.main}>
        <QuestionHeader />
        <Container sx={{ flex: 1, mt: 5 }}>
          <ReferralStateAlert context={query} />
        </Container>
      </div>
    );

  if (!info?.service_name && services?.length === 0)
    return (
      <div className={styles.main}>
        <QuestionHeader />
        <Container sx={{ flex: 1, minHeight: "100vh" }}>
          <InvalidServiceAlert />
        </Container>
      </div>
    );

  return (
    <div className={styles.main}>
      <Head>
        <title>Thrivelab Booking</title>
        <meta name="description" content="Book your visit" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <Script
        id="gtm-layer"
        async
        src="https://www.googletagmanager.com/gtag/js?id=G-M3GRWQG3R0"
      >{`
window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', 'G-M3GRWQG3R0');;
`}</Script>
      <QuestionHeader />
      <Container sx={{ flex: 1, my: "auto", minHeight: "50vh" }}>
        {showState && (
          <Box>
            <Typography
              variant="h4"
              color="primary"
              align="center"
              sx={{ fontFamily: "JazminAlt", fontSize: "35px" }}
            >{`Let's connect you with a provider`}</Typography>
            <Typography
              variant="subtitle1"
              color="primary"
              align="center"
              sx={{
                opacity: 0.8,
                mt: 3,
                mb: 2,
                fontFamily: "ABCDiaTypeRegular",
              }}
            >
              Please select your state to check the availability of providers in
              your area.
            </Typography>
            <States
              onChange={(state) =>
                push(
                  `/services/${(query?.service as string[])?.join("/")}?state=${state}&${generateQsString()}`,
                )
              }
            />
          </Box>
        )}
        {showInsurance && (
          <InsuranceList
            onSubmit={onInsuranceSubmitted}
            onSelected={onInsuranceSelected}
            // }
          />
        )}
        {showPicker && (
          <>
            <Box>
              <Stack direction="column" spacing={2}>
                {timeZone && (
                  <SchedulePicker
                    dpc={dpc}
                    staff={services}
                    timeZone={timeZone.name}
                    useAll={useAll}
                    onChange={handleChange}
                    info={info}
                    state={String(selectedState)}
                  />
                )}
                {!consult?.loading && (
                  <>
                    {showProviders && (
                      <Staff
                        dpcs={services}
                        dpc={dpc}
                        onChange={(dpc) => handleSetDpc(dpc)}
                      />
                    )}
                    {timeZone && (
                      <TimeZones
                        onChange={(tz) => handleChangeTz(tz!)}
                        defaultTimeZone={timeZone}
                      />
                    )}
                  </>
                )}
              </Stack>
            </Box>
            {!consult.loading && (
              <AvailableSlots
                isITH={isITH}
                slots={consult.slots}
                isEmpty={
                  (consult.slots.length === 0 && consult.loading === false) ??
                  false
                }
                timeZone={timeZone?.name!}
                dpc={dpc!}
                isLoading={consult.loading}
                info={info}
                freeConsult={freeConsult}
                repick={Boolean(query?.repick)}
                preloaded={patient !== undefined}
                reschedule={false}
                selectedDay={consult.selectedDate}
                serviceId={
                  services.length > 0 ? services[0].service_id : undefined
                }
              />
            )}
          </>
        )}
      </Container>
    </div>
  );
};

export async function getServerSideProps(context: any) {
  const getKey = (
    key: "patientid" | "state" | "email" | "payor" | "utm_source",
  ) => {
    const qs = Object.keys(context.query) ?? [];
    const vals = Object.values(context.query) ?? [];
    const data = qs?.find((q) => q?.toLowerCase() === key);
    return data ? (vals[qs.indexOf(data)] as string) : null;
  };
  if (context.query.service) {
    const {
      [0]: serviceId,
      [1]: staffId,
      [2]: patientId,
    } = context.query.service;

    const isITH = serviceId === "4626892000473035255";
    const token = await generateToken(serviceId);
    let services = [];
    const patientid = getKey("patientid");
    const state = getKey("state");
    const payor = getKey("payor");

    services =
      (await getServices({
        token,
        serviceId,
        staffId: staffId ?? null,
        patientId: patientId ?? patientid ?? "",
        state: state ?? null,
        payor: payor ?? null,
        utm_source: getKey("utm_source") ?? null,
      })) ?? [];

    let patient: Partial<ServiceCommon> = {
      first_name: "",
      last_name: "",
      patientId: null,
    };
    if (
      services &&
      services["patient information"]?.email &&
      String(context?.query?.patientid) !== ""
    ) {
      patient = await getPatientInfo(
        token?.accessToken,
        services["patient information"].email,
        context?.query.patientid,
      );
    }

    const info = services["service information"] ?? {};
    const hxCancer = context.query?.hxCancer === "true";

    const sanitizeForSerialization = (obj: any): any => {
      if (obj === null || obj === undefined) return null;
      if (typeof obj !== 'object') return obj;
      if (Array.isArray(obj)) return obj.map(sanitizeForSerialization);
      
      const sanitized: any = {};
      for (const [key, value] of Object.entries(obj)) {
        sanitized[key] = value === undefined ? null : sanitizeForSerialization(value);
      }
      return sanitized;
    };

    return {
      props: {
        services: services["Available Services"]
          ? services["Available Services"][0]
          : [],
        patient: patient
          ? { ...patient, patientId: context?.query.patientid ?? null }
          : null,
        info: sanitizeForSerialization(
          context.query?.dpc && Boolean(context.query.dpc) === true
            ? { ...info, Cost: 0 }
            : {
                ...info,
                Minimum_Booking_Notice_Min: hxCancer
                  ? 1440
                  : info.Minimum_Booking_Notice_Min ?? null,
              }
        ),
        staffId: staffId ?? null,
        isITH: isITH,
      },
    };
  }
  return {
    props: {
      services: [],
      patient: null,
      info: null,
      staffId: null,
      isITH: false,
    },
  };
}

export default PickSlot;
